import { useSelector } from 'react-redux';
import { RTI_DOWNSTREAM_SHARE } from '@rsos/constants/collaborator';
import {
  EmergencyLocation,
  EmergencyCallLocation,
  EmergencyCallSecondaryLocation,
} from './emergencyCalls.types';
import { EmergencyCallsState } from './emergencyCallsSlice';

/**
 * Determines if a call status should be updated in the unlikely event where the ALI location
 * payload from Hermes is not in order.
 * @param {Object} previousLocation - most recent location payload
 * @param {Object} currentLocation - current ALI location payload
 * @returns Boolean
 */
export const shouldUpdateCallStatus = (
  previousLocation: EmergencyLocation | EmergencyCallSecondaryLocation,
  currentLocation: EmergencyLocation | EmergencyCallSecondaryLocation,
) => {
  return (
    !!(
      !previousLocation &&
      currentLocation &&
      'call_status' in currentLocation &&
      currentLocation?.call_status
    ) ||
    !!(
      previousLocation &&
      currentLocation &&
      'call_status' in currentLocation &&
      currentLocation?.call_status &&
      currentLocation.created_time > previousLocation.created_time
    )
  );
};

export const useGetSelectedCollaborator = () => {
  const selectedCallerID = useSelector(
    (state: { emergencyCalls: EmergencyCallsState }) =>
      state.emergencyCalls.selectedCallerID,
  );

  const collaborator = useSelector(
    (state: { emergencyCalls: EmergencyCallsState }) => {
      if (!selectedCallerID) {
        return null;
      }
      return state.emergencyCalls.callQueue[selectedCallerID]?.collaborator;
    },
  );

  return (
    collaborator || {
      conversationID: null,
      pairedAccountID: null,
      type: null,
      isUnread: false,
      pairedAccountName: null,
      dispatcherPosition: null,
      callEnded: false,
    }
  );
};

/**
 * rti_downstream sends the identical share event to every recipient,
 * including the primary agency's own copy -- pairedAccountID always names
 * the actual primary agency, even when the primary is the one reading it.
 * Only downstream/secondary recipients should see the "pushed call"
 * treatment; the primary's own view of its own call should not.
 *
 * currentAccountId is required (not just falsy-checked as "unknown"): if the
 * current agency's own account id hasn't hydrated yet, treat this as NOT a
 * recipient (i.e. suppress) rather than defaulting to true -- showing the
 * secondary-facing treatment to what might actually be the primary agency,
 * even briefly, is worse than the treatment being briefly absent.
 */
export const isRtiDownstreamRecipient = (
  collaborator:
    | { type: string | null; pairedAccountID: string | null }
    | null
    | undefined,
  currentAccountId: string | null | undefined,
) => {
  return (
    collaborator?.type === RTI_DOWNSTREAM_SHARE &&
    !!currentAccountId &&
    collaborator?.pairedAccountID !== currentAccountId
  );
};

/**
 * Returns `collaborator` unchanged, or `null` when it's the primary agency's
 * own suppressed copy of an rti_downstream share event (see
 * isRtiDownstreamRecipient). Every field a component reads off `collaborator`
 * -- type, isUnread, pairedAccountName, etc. -- should come from this value
 * rather than the raw selector result, so suppression can't be forgotten for
 * one field while applied to another (e.g. gating the banner/icon but
 * leaving isUnread able to highlight a queue row with nothing shown to
 * explain why).
 */
export const getEffectiveCollaborator = <
  T extends
    | { type: string | null; pairedAccountID: string | null }
    | null
    | undefined,
>(
  collaborator: T,
  currentAccountId: string | null | undefined,
): T | null => {
  const isSuppressedRtiDownstream =
    collaborator?.type === RTI_DOWNSTREAM_SHARE &&
    !isRtiDownstreamRecipient(collaborator, currentAccountId);
  return isSuppressedRtiDownstream ? null : (collaborator ?? null);
};

export const isEmergencyCallLocation = (
  location: EmergencyLocation,
): location is EmergencyCallLocation => {
  return (
    Object.hasOwnProperty.call(location, 'latitude') &&
    Object.hasOwnProperty.call(location, 'longitude')
  );
};
